import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import GoogleAuthService from '@dashboard/services/googleAuth';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { tracking } from '@dashboard/lib/tracking';
import Spinner from '@dashboard/components/Spinner';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import Meta from '@dashboard/components/Meta';
import Form from '@dashboard/components/Formik/Form';
import Button from '@/dashboard/components/Button';
import Link from '@/dashboard/components/Link';

const GoogleCallback = () => {
  const location = useLocation();
  const history = useHistory();
  const { setLogged } = useAuth();
  const [error, setError] = useState('');
  const [showAccountLinking, setShowAccountLinking] = useState(false);
  const [linkingData, setLinkingData] = useState(null);
  const [failedRedirectPath, setFailedRedirectPath] = useState('/login');

  function handleFailedRedirect() {
    const urlParams = new URLSearchParams(location.search);
    const state = urlParams.get('state');
    const parsedState = GoogleAuthService.parseState(state);
    const invite = parsedState?.invite;
    const team = parsedState?.team;
    const returnPath = parsedState?.returnPath;

    if (invite) {
      setFailedRedirectPath(`/accept-invite?invite=${invite}&team=${team}`);
    } else if (returnPath) {
      setFailedRedirectPath(returnPath);
    } else {
      setFailedRedirectPath('/login');
    }
  }

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        if (error) {
          console.error('Google OAuth error:', error);
          setError(`Authentication failed: ${error}`);
          handleFailedRedirect();
          return;
        }

        if (!code) {
          console.error('No authorization code received from Google');
          setError('No authorization code received from Google');
          handleFailedRedirect();
          return;
        }

        // Get stored OAuth parameters
        const mode = localStorage.getItem('google_oauth_mode') || 'login';
        const marketingConsent = localStorage.getItem('google_oauth_marketing_consent') === 'true';
        const gclid = localStorage.getItem('google_oauth_gclid');
        const sessionId = localStorage.getItem('google_oauth_sessionId');

        // Clean up localStorage
        localStorage.removeItem('google_oauth_mode');
        localStorage.removeItem('google_oauth_marketing_consent');
        localStorage.removeItem('google_oauth_gclid');
        localStorage.removeItem('google_oauth_sessionId');

        // Parse state to get redirect info and invite token
        const parsedState = GoogleAuthService.parseState(state);
        const redirectUrl = parsedState?.redirectPath || '/dashboard';
        const invite = parsedState?.invite;

        const redirectURI = GoogleAuthService.getRedirectURI();

        // Choose the appropriate endpoint based on mode
        let response;
        if (mode === 'invite' && invite) {
          response = await GoogleAuthService.acceptInvite(
            code,
            redirectURI,
            invite,
            marketingConsent,
            gclid,
            sessionId,
          );
        } else {
          response = await GoogleAuthService.login(
            code,
            redirectURI,
            marketingConsent,
            invite,
            gclid,
            sessionId,
          );
        }

        if (response.requiresAccountLinking) {
          // Show account linking UI
          setLinkingData({
            googleProfile: response.googleProfile,
            email: response.email,
            hasInvite: response.hasInvite,
            invite: invite,
            message: response.message,
            redirectUrl,
          });
          setShowAccountLinking(true);
        } else if (response.auth && response.data) {
          // Successful authentication
          setLogged({
            user: response.data.user,
            seller: response.data.user.seller,
            token: response.data.token,
          });

          // Track signup event for new users
          if (response.data.isNewUser) {
            tracking.identifyUser(response.data.user._id);
            tracking.trackEvent('Signup', {
              currency: 'USD',
              predicted_ltv: 600,
              userId: response.data.user._id,
              email: response.data.user.email,
              method: 'google',
            });
            tracking.setUser({
              $email: response.data.user.email,
              $name: response.data.user.name,
              $created: new Date(),
              $seller: response.data.user.seller?._id,
            });
          }

          // Redirect new users to the onboarding flow (same as normal signup)
          let finalRedirectUrl = redirectUrl;
          if (response.data.isNewUser && !response.data.inviteAccepted) {
            finalRedirectUrl = '/dashboard/seller/start?step=1';
          }

          history.push(finalRedirectUrl);
        } else {
          console.error('Unexpected response from server:', response);
          throw new Error('Unexpected response from server');
        }
      } catch (error) {
        console.error('Google OAuth callback error:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response?.data,
        });
        setError(error.message || 'Authentication failed');
        handleFailedRedirect();
      }
    };

    handleOAuthCallback();
  }, [location, history, setLogged]);

  if (error) {
    return (
      <>
        <Meta meta={{ title: 'Authentication failed' }} />
        <AuthLayout>
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Authentication failed</h1>
            <p className="text-gray-600 mb-8 text-lg">{error}</p>
            <Button href={failedRedirectPath} appearance="primary">
              Try again
            </Button>
          </div>
        </AuthLayout>
      </>
    );
  }

  if (showAccountLinking && linkingData) {
    return (
      <>
        <Meta
          meta={{
            title: linkingData.hasInvite ? 'Link account & join team' : 'Link Google account',
          }}
        />
        <AuthLayout>
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              {linkingData.hasInvite ? 'Link account & join team' : 'Link Google account'}
            </h1>
            <p className="text-gray-600 text-lg">{linkingData.message}</p>
          </div>

          <Form
            initialValues={{
              email: linkingData.email,
              password: '',
            }}
            onSubmit={async values => {
              try {
                const response = await GoogleAuthService.linkAccount(
                  linkingData.googleProfile,
                  linkingData.email,
                  values.password,
                  linkingData.invite,
                );

                if (response.auth && response.data) {
                  setLogged({
                    user: response.data.user,
                    seller: response.data.user.seller,
                    token: response.data.token,
                  });

                  history.push(linkingData.redirectUrl);
                }

                return {
                  data: response.data,
                  error: response.error || response.message,
                };
              } catch (error) {
                console.error('Account linking error:', error);
                return {
                  data: null,
                  error: error.message || 'Failed to link account',
                };
              }
            }}
            showState={false}
            fields={[
              {
                name: 'email',
                type: 'email',
                label: 'Email',
                disabled: true,
                className: '!bg-gray-50 !text-gray-500',
              },
              {
                name: 'password',
                type: 'password',
                label: 'Password',
                placeholder: 'Enter your password to confirm',
                required: true,
              },
            ]}
            submitButton={{
              label: 'Link account',
              appearance: 'primary',
              size: 'full',
            }}
          />

          <div className="text-center mt-4">
            <Link href={'/login'} variant="secondary">
              Cancel
            </Link>
          </div>
        </AuthLayout>
      </>
    );
  }

  return (
    <>
      <Meta meta={{ title: 'Authenticating...' }} />
      <AuthLayout>
        <div className="text-center">
          <div className="mb-8">
            <div className="flex justify-center mb-6">
              <Spinner size="large" appearance="dark" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Authenticating...</h1>
            <p className="text-gray-600 text-lg">Processing your Google sign-in</p>
          </div>
        </div>
      </AuthLayout>
    </>
  );
};

export default GoogleCallback;
